import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useAuthTenant } from 'store/auth'
import { getPreviewPublic, getPreviewProtected } from 'services/transparency-service'
import { PreviewBanner } from './PreviewBanner'

import { TransparencyHeader } from '../Public/components/TransparencyHeader'
import { FrameworkBadges } from '../Public/components/FrameworkBadges'
import { GovernanceOverview } from '../Public/components/GovernanceOverview'
import { TrendIndicator } from '../Public/components/TrendIndicator'
import { PublicPolicies } from '../Public/components/PublicPolicies'
import { OverviewTab } from '../Protected/components/OverviewTab'
import { FrameworksTab } from '../Protected/components/FrameworksTab'
import { GovernanceStructure } from '../Protected/components/GovernanceStructure'
import { PoliciesTab } from '../Protected/components/PoliciesTab'
import {
  TransparencyPageContainer,
  TransparencyPageContent,
  SectionTitle,
} from '../Public/styles'
import {
  Spinner,
  LoadingContainer,
  ProtectedHero,
  ProtectedHeroInner,
  ProtectedHeroLabel,
  ProtectedHeroLabelText,
  ProtectedHeroTitle,
  ProtectedHeroCaption,
  ExternalContent,
  PillTabBar,
  PillTab,
  TabContent,
} from '../Protected/styles'

import styled from 'styled-components'

const PageWrapper = styled.div`
  background: var(--vo-mist);
  min-height: 100vh;
`

const ErrorBox = styled.div`
  padding: 32px;
  text-align: center;
`

const ErrorMessage = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-slate);
  margin: 0;
`

const ShieldSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--vo-capri)">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
  </svg>
)

/**
 * TransparencyPreview - Preview Mode (Story-009)
 * - Internal users only (JWT required)
 * - Simulates public and protected views
 * - Does not generate audit logs
 * - Shows "Preview Mode" banner
 */
export const TransparencyPreview = () => {
  const [viewMode, setViewMode] = useState('public')
  const selectedTenant = useSelector(useAuthTenant)
  const companyKey = selectedTenant?.slug

  return (
    <PageWrapper>
      <PreviewBanner viewMode={viewMode} onViewModeChange={setViewMode} />

      {viewMode === 'public' ? (
        <PublicPreview companyKey={companyKey} />
      ) : (
        <ProtectedPreview companyKey={companyKey} />
      )}
    </PageWrapper>
  )
}

/**
 * PublicPreview - Preview of public transparency view (uses preview API)
 */
const PublicPreview = ({ companyKey }) => {
  const { t } = useTranslation('transparency')
  const { isLoading, error, data } = useQuery(
    ['previewPublic', companyKey],
    () => getPreviewPublic(companyKey),
    { enabled: !!companyKey }
  )

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    )
  }

  if (error || !data?.data) {
    return (
      <ErrorBox>
        <ErrorMessage>{t('preview.errorLoading')}</ErrorMessage>
      </ErrorBox>
    )
  }

  const { organization, frameworkBadges, trendIndicator, governanceStructure, policies } = data.data

  return (
    <TransparencyPageContainer>
      <TransparencyHeader
        name={organization?.name}
        description={organization?.description}
        lastUpdated={organization?.lastUpdated}
      />

      <TransparencyPageContent>
        {trendIndicator && (
          <TrendIndicator
            label={trendIndicator.label}
            description={trendIndicator.description}
            trend={trendIndicator.trend}
          />
        )}

        {frameworkBadges && frameworkBadges.length > 0 && (
          <div style={{ marginBottom: 36 }}>
            <SectionTitle>{t('shared.commitments')}</SectionTitle>
            <FrameworkBadges badges={frameworkBadges} />
          </div>
        )}

        {governanceStructure && (
          <div style={{ marginBottom: 36 }}>
            <SectionTitle>{t('shared.governanceStructure')}</SectionTitle>
            <GovernanceOverview
              categories={governanceStructure.categories}
              overallPosture={governanceStructure.overallPosture}
            />
          </div>
        )}

        {policies && policies.length > 0 && (
          <div>
            <SectionTitle>{t('shared.publicPolicies')}</SectionTitle>
            <PublicPolicies policies={policies} />
          </div>
        )}
      </TransparencyPageContent>
    </TransparencyPageContainer>
  )
}

/**
 * ProtectedPreview - Preview of protected transparency view (uses preview API)
 */
const ProtectedPreview = ({ companyKey }) => {
  const { t } = useTranslation('transparency')
  const [activeTab, setActiveTab] = useState('overview')

  const { isLoading, error, data } = useQuery(
    ['previewProtected', companyKey],
    () => getPreviewProtected(companyKey),
    { enabled: !!companyKey }
  )

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    )
  }

  if (error || !data?.data) {
    return (
      <ErrorBox>
        <ErrorMessage>{t('preview.errorLoading')}</ErrorMessage>
      </ErrorBox>
    )
  }

  const { organization, frameworks, governanceStructure, policies } = data.data

  const tabs = [
    { key: 'overview', label: t('shared.tabs.overview') },
    { key: 'frameworks', label: t('shared.tabs.frameworks') },
    { key: 'governance', label: t('shared.tabs.governance') },
    { key: 'policies', label: t('shared.tabs.policies') },
  ]

  return (
    <>
      <ProtectedHero>
        <ProtectedHeroInner>
          <ProtectedHeroLabel>
            <ShieldSvg />
            <ProtectedHeroLabelText>{t('protected.accessProtected')}</ProtectedHeroLabelText>
          </ProtectedHeroLabel>
          <ProtectedHeroTitle>{organization?.name}</ProtectedHeroTitle>
          <ProtectedHeroCaption>{t('shared.transparencyCenter')}</ProtectedHeroCaption>
        </ProtectedHeroInner>
      </ProtectedHero>

      <ExternalContent>
        <PillTabBar>
          {tabs.map(tab => (
            <PillTab
              key={tab.key}
              $active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </PillTab>
          ))}
        </PillTabBar>

        <TabContent>
          {activeTab === 'overview' && (
            <OverviewTab
              organization={organization}
              frameworks={frameworks}
              governanceStructure={governanceStructure}
            />
          )}
          {activeTab === 'frameworks' && (
            <FrameworksTab
              frameworks={frameworks}
              companyKey={companyKey}
              accessToken={null}
              isPreview
            />
          )}
          {activeTab === 'governance' && (
            <GovernanceStructure
              categories={governanceStructure?.categories}
              overallPosture={governanceStructure?.overallPosture}
              showSubDomains={true}
            />
          )}
          {activeTab === 'policies' && (
            <PoliciesTab
              policies={policies}
              companyKey={companyKey}
              accessToken={null}
            />
          )}
        </TabContent>
      </ExternalContent>
    </>
  )
}

export { PreviewBanner }
